import { Row } from 'react-bootstrap';
import { useCallback, useEffect, useState } from 'react';
import Pathway from './Pathway/Pathway';
import { loadConfig } from '../../config';
import { useSelector } from 'react-redux';
import { useContext } from 'react';
import { ProfileContext } from 'pages/Profile';

const normalizePathwayAssignments = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.userPathways)) return payload.userPathways;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const flattenProfilePathwaysProgress = (profile) => {
  const instances = Array.isArray(profile?.pathwaysProgressByInstance)
    ? profile.pathwaysProgressByInstance
    : [];

  return instances.flatMap((instanceEntry) => {
    const instanceId = String(instanceEntry?.instanceId || '').trim();
    const pathways = Array.isArray(instanceEntry?.pathways) ? instanceEntry.pathways : [];

    return pathways.map((entry) => ({
      ...entry,
      _id: String(entry?._id || `${profile?._id || 'user'}-${instanceId || 'instance'}-${entry?.pathwayId || ''}`).trim(),
      user: String(profile?._id || ''),
      instanceId,
      pathwayId: String(entry?.pathwayId || entry?.pathway?._id || '').trim(),
      pathway: entry?.pathway || null,
      projects: Array.isArray(entry?.projects) ? entry.projects : []
    }));
  });
};

export default function Pathways({ tabActive }) {
  const { user: authUser } = useSelector(x => x.auth);
  const { profile } = useContext(ProfileContext);

  const [isLoading, setIsLoading] = useState(false);
  const [pathways, setPathways] = useState([]);
  const [assignedPathways, setAssignedPathways] = useState([]);
  const [pathwaysUrl, setPathwaysUrl] = useState('');
  const [tmPathwaysUrl, setTmPathwaysUrl] = useState('');
  const [bootcampUrl, setBootcampUrl] = useState('');

  const apiUrl = process.env.REACT_APP_URL;

  useEffect(() => {
    loadConfig().then(config => {
      setPathwaysUrl(config.PATHWAYS_URL);
      setTmPathwaysUrl(config.TM_PATHWAYS_URL);
      setBootcampUrl(config.BOOTCAMP_URL);
    });
  }, []);

  const getPathways = useCallback(async ({ showLoader = true } = {}) => {
    if (!pathwaysUrl) return;
    if (showLoader) setIsLoading(true);
    try {
      const response = await fetch(`${pathwaysUrl}/pathways`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setPathways(data);
    } catch (error) {
      console.error('Error fetching pathways: ', error);
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, [pathwaysUrl]);

  useEffect(() => {
    if (tabActive) {
      getPathways();
    }
  }, [getPathways, tabActive]);

  const getAssignedPathways = useCallback(async ({ preferProfile = true } = {}) => {
    if (!profile?._id || !authUser?.token) return;
    const profileProgressAssignments = flattenProfilePathwaysProgress(profile);
    if (preferProfile && profileProgressAssignments.length > 0) {
      setAssignedPathways(profileProgressAssignments);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/userpathway/${profile._id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser.token}`
        }
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(String(data?.error || data?.Error || `Failed to fetch pathway assignments (${response.status})`));
      }
      const normalized = normalizePathwayAssignments(data);
      const profileAssignments = Array.isArray(profile?.pathwayAssignments) ? profile.pathwayAssignments : [];
      setAssignedPathways(normalized.length > 0 ? normalized : profileAssignments);
    } catch (error) {
      console.error('Failed to fetch profile pathway assignments:', error?.message || error);
      const profileAssignments = Array.isArray(profile?.pathwayAssignments) ? profile.pathwayAssignments : [];
      setAssignedPathways(profileAssignments);
    }
  }, [apiUrl, authUser?.token, profile]);

  useEffect(() => {
    if (!tabActive) return;
    getAssignedPathways();
  }, [getAssignedPathways, tabActive]);

  const reloadPathways = useCallback(async () => {
    await Promise.all([
      getPathways({ showLoader: false }),
      getAssignedPathways({ preferProfile: false })
    ]);
  }, [getAssignedPathways, getPathways]);

  // Separate and sort pathways
  const pathwayType = pathways
    .filter(p => p.type === 'pathway')
    .sort((a, b) => a.name.localeCompare(b.name));
  const legacyType = pathways
    .filter(p => p.type === 'legacy')
    .sort((a, b) => a.name.localeCompare(b.name));
  const nonPathwayType = pathways
    .filter(p => p.type === 'non-pathway')
    .sort((a, b) => a.name.localeCompare(b.name));

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const noAssignedPathways = Array.isArray(assignedPathways) && assignedPathways.length === 0;
  const pathwaysSourceError = String(profile?.pathwayAssignmentsError || '').trim();

  return (
    <>
      <h1 className='text-center mt-5 mb-5'>IMPORTANT!</h1>
      <h5 className='text-center mb-3'>Before selecting the pathway in this page, make sure you have already added and paid for it in the <a href={tmPathwaysUrl} target='_blank' rel='noreferrer noopener' style={{ color: '#D38C50', fontWeight: 'bold', fontSize: '1.4rem' }}>Toastmasters International website</a> and is <strong>visible</strong> in <a href={bootcampUrl} target='_blank' rel='noreferrer noopener' style={{ color: '#D38C50', fontWeight: 'bold', fontSize: '1.4rem' }}>Toastmasters International Bootcamp</a>.</h5>
      <p>It is recommended to subscribe for Paths, while the Legacy Paths are available only for those who have selected these in the past and are still in progress with them. The Legacy Paths are going to be removed soon.</p>
      {noAssignedPathways && pathwaysSourceError && (
        <p style={{ color: '#f9a825', fontWeight: 600 }}>
          {pathwaysSourceError}
        </p>
      )}
      {
        pathwayType.length > 0 && (
          <>
            <h2 className='text-center mt-5 mb-5'>Paths</h2>
            <Row className="justify-content-center">
              {pathwayType.map(pathway => (
                <Pathway key={pathway._id} pathway={pathway} assignedPathways={assignedPathways} onReload={reloadPathways} />
              ))}
            </Row>
          </>
        )
      }
      {
        legacyType.length > 0 && (
          <>
            <h2 className='text-center mt-5 mb-5'>Legacy Paths</h2>
            <Row className="justify-content-center">
              {legacyType.map(pathway => (
                <Pathway key={pathway._id} pathway={pathway} assignedPathways={assignedPathways} onReload={reloadPathways} />
              ))}
            </Row>
          </>
        )
      }
      {
        nonPathwayType.length > 0 && (
          <>
            <h2 className='text-center mt-5 mb-5'>Non-Pathway</h2>
            <Row className="justify-content-center">
              {nonPathwayType.map(pathway => (
                <Pathway key={pathway._id} pathway={pathway} assignedPathways={assignedPathways} onReload={reloadPathways} />
              ))}
            </Row>
          </>
        )
      }
    </>
  );
}